"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, FileText, Eye, Mail, Database, Scale, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const policySections = [{
  id: "collection",
  title: "1. Information we collect",
  summary: "We collect information necessary to deliver research publications, verify executive readership, and improve editorial depth.",
  details: "When you access MinfoMedia reports, whitepapers, or newsletters, we record: (a) contact information you provide willingly, including name, corporate email address, job title, and organization; (b) reader activity metrics such as document downloads, session durations, and article bookmarks; and (c) technical access logs containing IP addresses, browser specifications, and referring domains."
}, {
  id: "lawful-basis",
  title: "2. Lawful basis for processing",
  summary: "Every processing action operates under clear legal grounds under global privacy frameworks including GDPR and CCPA.",
  details: "We process your data under three distinct lawful bases: (1) Legitimate interest in distributing business analysis, maintaining publication integrity, and preventing unauthorized scraping; (2) Contractual necessity when fulfilling subscription agreements or report deliveries; and (3) Explicit consent for specialized editorial briefings and event invitations."
}, {
  id: "usage",
  title: "3. Operational use cases",
  summary: "Your data is used strictly for editorial delivery, access verification, and publication quality enhancement.",
  details: "We do not sell, rent, or trade reader personal information to third-party advertisers or data brokers. Collected information is deployed solely to: generate personalized research digests, verify qualification for gated industry benchmarks, authenticate subscriber access tiers, and aggregate anonymized readership statistics for editorial planning."
}, {
  id: "retention",
  title: "4. Data retention and storage",
  summary: "Reader records are retained only as long as necessary for active subscriptions and regulatory compliance.",
  details: "Subscriber profiles remain active throughout your subscription lifecycle and are archived after 24 months of total inactivity. Server access logs and telemetry data are permanently purged after 90 days. Encrypted backups follow strict rotating deletion schedules compliant with SOC 2 requirements."
}, {
  id: "rights",
  title: "5. Your data subject rights",
  summary: "You hold full statutory control over your personal records at all times.",
  details: "Regardless of geographic location, MinfoMedia affords all readers the right to: request a complete export of stored personal data, correct inaccurate professional details, object to specific processing streams, or request total account erasure. Requests are acknowledged within 48 business hours."
}, {
  id: "security",
  title: "6. Security architecture",
  summary: "Enterprise-grade technical safeguards secure our research repository and subscriber database.",
  details: "All web traffic, report downloads, and administrative sessions are enforced via TLS 1.3 encryption. At-rest databases employ AES-256 encryption with automated vulnerability scanning, restricted role-based employee access, and routine independent security audits."
}];
const highlights = [{
  iconName: "ShieldCheck",
  label: "No sponsored data sales",
  desc: "We never monetize reader data through third-party ad networks."
}, {
  iconName: "Lock",
  label: "End-to-end encryption",
  desc: "TLS 1.3 in transit and AES-256 at rest across all research systems."
}, {
  iconName: "Scale",
  label: "Global compliance",
  desc: "Structured in full alignment with GDPR, CCPA, and international standards."
}];
function RenderIcon({
  name
}: {
  name: string;
}) {
  const props = {
    className: "size-5 text-accent-foreground shrink-0",
    "aria-hidden": true
  };
  if (name === "ShieldCheck") return <ShieldCheck {...props} />;
  if (name === "Lock") return <Lock {...props} />;
  if (name === "Scale") return <Scale {...props} />;
  return <FileText {...props} />;
}
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.15
    },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="privacy-policy" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-[840px] px-5 sm:px-8">
        {/* Editorial Masthead Header */}
        <motion.div {...animationProps} className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground bg-muted px-2.5 py-1 rounded-sm">
              Legal disclosure
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden="true" />
              Effective Date: March 15, 2025
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Privacy policy and data protection standards
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            MinfoMedia is committed to transparent, responsible stewardship of
            reader records. This policy details our precise data practices,
            lawful processing grounds, and your individual rights as a reader or
            research subscriber.
          </p>

          {/* Masthead rule motif */}
          <div className="pt-2 pb-6">
            <div className="h-[1px] bg-foreground w-full" />
            <div className="h-[0.5px] bg-border w-full mt-[2px]" />
          </div>
        </motion.div>

        {/* Highlight Metrics / Guarantees Strip */}
        <motion.div {...animationProps} className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8">
          {highlights.map((item, i) => <div key={item.label} data-index={i} className="bg-card border border-border rounded-md p-4 flex flex-col gap-2 shadow-2xs">
              <div className="size-9 rounded-sm bg-primary flex items-center justify-center">
                <RenderIcon name={item.iconName} />
              </div>
              <h2 className="font-sans text-sm font-semibold text-foreground">
                {item.label}
              </h2>
              <p className="text-xs text-muted-foreground leading-normal">
                {item.desc}
              </p>
            </div>)}
        </motion.div>

        {/* In-depth Legal Content Sections */}
        <div className="space-y-10 pt-4">
          {policySections.map((section, i) => <motion.article key={section.id} data-index={i} {...animationProps} className="border-b border-border pb-8 last:border-b-0 space-y-3">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                {section.title}
              </h2>
              <p className="text-sm font-medium text-foreground bg-muted/60 p-3 rounded-sm border-l-2 border-primary">
                {section.summary}
              </p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1">
                {section.details}
              </p>
            </motion.article>)}
        </div>

        {/* Data Protection Officer Contact Card */}
        <motion.div {...animationProps} className="mt-12">
          <Card className="border border-border rounded-md shadow-xs bg-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 text-primary">
                <Database className="size-5" aria-hidden="true" />
                <CardTitle className="font-serif text-lg sm:text-xl">
                  Data Protection Officer contact
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                If you have questions concerning our processing methods, wish to
                exercise your data subject rights, or require an export of your
                subscriber history, reach our privacy governance team directly.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <p className="font-semibold text-foreground">
                    MinfoMedia Privacy & Compliance
                  </p>
                  <p>123 Main Street, Springfield, USA</p>
                  <p>Inquiries: info@minfomedia.com</p>
                </div>

                <div className="flex items-center gap-3">
                  <Button asChild variant="outline" size="sm">
                    <a href="mailto:info@minfomedia.com">
                      <Mail className="size-4 mr-1.5" aria-hidden="true" />
                      Email privacy team
                    </a>
                  </Button>
                  <Button asChild variant="default" size="sm">
                    <Link to="/TermsConditionsPage">
                      View terms
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}